<script setup lang="ts">
import {
  auctionPlatformSearchTabs,
  nonAuctionPlatformSearchTabs,
  SearchResultTypes,
} from "~/types/Search";

type Props = {
  searchTerm: string;
  tab: SearchResultTypes;
};

const props = defineProps<Props>();
const searchTerm = computed(() => props.searchTerm);
const tab = computed(() => props.tab);

const router = useRouter();
const { t } = useI18n();
const localePath = useLocalePath();

const platformStore = usePlatformStore();
const isAuctionsEnabled = computed(
  () => platformStore.getPlatformConfiguration.isAuctionsEnabled,
);

const allowedTabs = computed(() =>
  isAuctionsEnabled.value
    ? auctionPlatformSearchTabs
    : nonAuctionPlatformSearchTabs,
);

const tabs = computed(() =>
  allowedTabs.value.map((tab) => ({
    id: tab,
    title: t(`searchResults.${tab}`),
  })),
);

const showFilterMenu = computed(() => {
  return (
    tab.value === SearchResultTypes.Events ||
    tab.value === SearchResultTypes.Products
  );
});

const onTabChange = (tabId: SearchResultTypes) => {
  const tabUrl = tabId !== SearchResultTypes.TopResults ? `/${tabId}` : "";
  router.replace(localePath({ path: `/search/${searchTerm.value}${tabUrl}` }));
};
</script>

<template>
  <div class="search-page__header--container">
    <div class="search-page__header--title-container block-container">
      <I18nT
        scope="global"
        keypath="searchResults.title"
        class="site-viewport-container text-platform-text-2 md:text-platform-text-1 md:text-3xl leading-none md:gap-2 uppercase"
        tag="h1"
      >
        <template #PLACEHOLDER_searchTerm>
          <span
            class="leading-none font-bold text-platform-color-1 md:leading-none md:font-normal ml-1 md:ml-0 uppercase"
          >
            "{{ searchTerm }}"
          </span>
        </template>
      </I18nT>
    </div>
    <BaseTabs
      :model-value="tab"
      :navigate="false"
      :tabs="tabs"
      :is-sticky-disabled="tab === SearchResultTypes.Products"
      @update:model-value="onTabChange"
    >
      <slot :key="`${searchTerm}-${tab}`" />
      <template
        v-if="showFilterMenu"
        #actions="{ active }"
      >
        <SearchResultPageEventFilter
          v-if="active === SearchResultTypes.Events"
          :search-term="searchTerm"
          data-cy="search-results-page-event-filter-button"
        />
      </template>
    </BaseTabs>
  </div>
</template>

<style scoped lang="postcss">
.search-page {
  &__header {
    &--container {
      @apply pt-6;
    }

    &--title {
      &-container {
        @apply mb-2 md:mb-6;
      }
    }

    &--buttons {
      @apply flex flex-row gap-2;
    }
  }
}
</style>
